import { useMemo } from "react";
import { UseQueryArgs, UseQueryExecute, UseQueryState } from "urql";

import { BlueprintEstimationResult } from "@/components/graphql/schema.graphql";
import {
  GetBlueprintEstimation,
  GetBlueprintEstimationQueryResult,
  GetBlueprintEstimationQueryVariables,
} from "@/graphql/blueprint.graphql";
import { ErrorWithDataFragment } from "@/hooks/queries.graphql";
import { ActionPricing } from "@/types";

import { usePreflightGated } from "./usePreflightGated";
import { useQueryWithResponseHandler } from "./useQueryWithResponseHandler";

export interface InputEntry {
  key: string;
  value: unknown;
}

interface UseBlueprintEstimationProps {
  blueprintVersionId?: string;
  runUntilNodeId?: string;
  resumeFromRunId?: string;
  runUntilExcludingTarget?: boolean;
  overrides?: Partial<
    UseQueryArgs<
      GetBlueprintEstimationQueryVariables,
      GetBlueprintEstimationQueryResult
    >
  >;
  inputValues?: InputEntry[];
  pause?: boolean;
}

type BlueprintEstimationWithDerivedGating = Omit<
  BlueprintEstimationResult,
  "actionPricing"
> & {
  actionPricing: ActionPricing;
};

interface UseBlueprintEstimationResults {
  estimation?: BlueprintEstimationWithDerivedGating;
  error?: ErrorWithDataFragment;
  execute: UseQueryExecute;
  state: UseQueryState<GetBlueprintEstimationQueryResult>;
}

export const useBlueprintEstimation = (
  props: UseBlueprintEstimationProps,
): UseBlueprintEstimationResults => {
  const {
    blueprintVersionId,
    runUntilNodeId,
    resumeFromRunId,
    runUntilExcludingTarget,
    overrides,
    inputValues,
    pause = false,
  } = props;

  const variables = useMemo(
    () => ({
      input: {
        blueprintVersionId,
      },
      estimationInput: {
        runUntilNodeId,
        resumeFromRunId,
        runUntilExcludingTarget,
        inputValues: inputValues?.map((entry) => ({
          key: entry.key,
          value: { value: entry.value },
        })),
      },
    }),
    [
      blueprintVersionId,
      runUntilNodeId,
      resumeFromRunId,
      runUntilExcludingTarget,
      inputValues,
    ],
  );

  const { data, execute, error, state } = useQueryWithResponseHandler({
    query: GetBlueprintEstimation,
    variables,
    requestPolicy: "cache-first",
    pause: !blueprintVersionId || pause,
    ...(overrides ?? {}),
  });

  const estimation = data?.blueprintVersion?.estimation;
  const actionPrice =
    estimation && estimation.__typename !== "Error"
      ? estimation.actionPricing?.actionPrice ?? null
      : null;
  const isGated = usePreflightGated(actionPrice);

  if (!estimation || error || estimation.__typename === "Error")
    return {
      estimation: undefined,
      execute,
      error:
        estimation?.__typename === "Error"
          ? { ...estimation, errorType: "INTERNAL_SERVER_ERROR" }
          : error,
      state,
    };

  return {
    estimation: {
      ...estimation,
      actionPricing: {
        actionPrice,
        isGated,
      },
    },
    execute,
    error,
    state,
  };
};
